import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/Api/axiosV2Config';
import { assertAttachmentAcceptable } from '@/features/delivery-proof/utils/attachments';
import type {
  CreateDeliveryProofDto,
  DeliveryProofAttachment,
  DeliveryProofResponseDto,
  ListDeliveryProofsDto,
  ListDeliveryProofsResponse,
  SendDeliveryProofDto,
  UpdateElementsDto,
  UpdateVehicleInfoDto,
  UpdateWarrantiesDto,
} from '@/features/delivery-proof/types/delivery-proof.types';

const BASE_URL = '/pro-v2/delivery-proofs';

// Query keys
export const deliveryProofKeys = {
  all: ['delivery-proofs'] as const,
  lists: () => [...deliveryProofKeys.all, 'list'] as const,
  list: (filters: ListDeliveryProofsDto) => [...deliveryProofKeys.lists(), filters] as const,
  details: () => [...deliveryProofKeys.all, 'detail'] as const,
  detail: (id: string) => [...deliveryProofKeys.details(), id] as const,
};

// API functions
export const deliveryProofApi = {
  create: async (data: CreateDeliveryProofDto): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.post(BASE_URL, data);
    return response.data;
  },

  getById: async (id: string): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  list: async (filters: ListDeliveryProofsDto): Promise<ListDeliveryProofsResponse> => {
    const response = await axiosInstance.get(BASE_URL, { params: filters });
    return response.data;
  },

  updateWarranties: async ({
    id,
    data,
  }: {
    id: string;
    data: UpdateWarrantiesDto;
  }): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.patch(`${BASE_URL}/${id}/warranties`, data);
    return response.data;
  },

  updateElements: async ({
    id,
    data,
  }: {
    id: string;
    data: UpdateElementsDto;
  }): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.patch(`${BASE_URL}/${id}/elements`, data);
    return response.data;
  },

  updateVehicleInfo: async ({
    id,
    data,
  }: {
    id: string;
    data: UpdateVehicleInfoDto;
  }): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.patch(`${BASE_URL}/${id}/vehicle-info`, data);
    return response.data;
  },

  uploadFile: async ({ id, file }: { id: string; file: File }): Promise<DeliveryProofAttachment> => {
    assertAttachmentAcceptable(file);

    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(`${BASE_URL}/${id}/upload-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Upload de 10 Mo puis conversion d'un PDF page par page côté API : les
      // 30 s par défaut laissent trop peu de marge sur une connexion lente.
      // Pas au-delà de 60 s, délai d'inactivité du load balancer AWS.
      timeout: 60000,
    });
    return response.data;
  },

  deleteFile: async ({ id, fileUrl }: { id: string; fileUrl: string }): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/${id}/files`, {
      params: { fileUrl },
    });
  },

  send: async ({ id, data }: { id: string; data: SendDeliveryProofDto }): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/send`, data);
    return response.data;
  },

  uploadSignedPdf: async ({ id, file }: { id: string; file: File }): Promise<DeliveryProofResponseDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(`${BASE_URL}/${id}/upload-signed`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  cancel: async (id: string): Promise<DeliveryProofResponseDto> => {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/cancel`);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/${id}`);
  },

  previewPdf: async (id: string): Promise<Blob> => {
    const response = await axiosInstance({
      method: 'post',
      url: `${BASE_URL}/${id}/preview-pdf`,
      responseType: 'blob',
    });
    return response.data;
  },

  downloadPdf: async (id: string, filename?: string): Promise<void> => {
    const response = await axiosInstance({
      method: 'get',
      url: `${BASE_URL}/${id}/download-pdf`,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'attestation-livraison.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// React Query hooks

export const useCreateDeliveryProof = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useDeliveryProof = (id: string | undefined) => {
  return useQuery({
    queryKey: deliveryProofKeys.detail(id!),
    queryFn: () => deliveryProofApi.getById(id!),
    enabled: !!id,
  });
};

export const useDeliveryProofList = (filters: ListDeliveryProofsDto) => {
  return useQuery({
    queryKey: deliveryProofKeys.list(filters),
    queryFn: () => deliveryProofApi.list(filters),
    refetchInterval: 120_000,
  });
};

export const useUpdateWarranties = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.updateWarranties,
    onSuccess: (data) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useUpdateElements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.updateElements,
    onSuccess: (data) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useUpdateVehicleInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.updateVehicleInfo,
    onSuccess: (data) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useUploadFile = () => {
  return useMutation({
    mutationFn: deliveryProofApi.uploadFile,
  });
};

export const useDeleteFile = () => {
  return useMutation({
    mutationFn: deliveryProofApi.deleteFile,
  });
};

export const useSendDeliveryProof = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.send,
    onSuccess: (data: DeliveryProofResponseDto) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useUploadSignedPdf = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.uploadSignedPdf,
    onSuccess: (data: DeliveryProofResponseDto) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useCancelDeliveryProof = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.cancel,
    onSuccess: (data) => {
      queryClient.setQueryData(deliveryProofKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

export const useDeliveryProofPreviewPdf = (id: string | undefined, enabled = false) => {
  return useQuery({
    queryKey: [...deliveryProofKeys.detail(id!), 'preview-pdf'] as const,
    queryFn: () => deliveryProofApi.previewPdf(id!),
    enabled: enabled && !!id,
    staleTime: 30 * 1000,
    cacheTime: 60 * 1000,
    retry: 1,
  });
};

export const useDeleteDeliveryProof = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deliveryProofApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryProofKeys.lists() });
    },
  });
};

